// Pure UAE access-level rules shared by server gates, entitlements and the client. No Nuxt/prisma deps.
import { FREE_TIER_LIMITS } from '~/utils/experiment/freeTier';
import { ALL_FEATURES, type Feature } from '~/utils/billing/featureTiers';

export const UAE_ACCESS_LEVELS = ['SIGNED_UP', 'VERIFIED', 'JOINED'] as const;
export type UaeAccessLevelValue = (typeof UAE_ACCESS_LEVELS)[number];

export const UAE_LEVEL_SOURCES = ['SIGNUP', 'ID_UPLOAD', 'ID_REJECTED', 'JOIN_APPROVED', 'ADMIN'] as const;
export type UaeLevelSource = (typeof UAE_LEVEL_SOURCES)[number];

export type UaeNextStep = 'verify_id' | 'join' | null;

export const MIN_UAE_LEVEL_REASON_LENGTH = 5;

const LEVEL_RANK: Record<UaeAccessLevelValue, number> = { SIGNED_UP: 1, VERIFIED: 2, JOINED: 3 };

export const isUaeAccessLevel = (value: unknown): value is UaeAccessLevelValue =>
  typeof value === 'string' && (UAE_ACCESS_LEVELS as readonly string[]).includes(value);

export type UaeLevelProfile =
  | { market?: string | null; uaeAccessLevel?: string | null; uaeLive?: boolean | null }
  | null
  | undefined;

// UAE programme agents (market UAE, not the partner path) get Pro access; partners keep the per-level limits.
export const isUaePartnerPath = (signupPath: string | null | undefined): boolean => signupPath === 'partner';

// Level applies only to market UAE (partners included); INT / UNKNOWN / missing → null (subscription gating).
export const effectiveUaeLevel = (profile: UaeLevelProfile): UaeAccessLevelValue | null => {
  if (profile?.market !== 'UAE') return null;
  return isUaeAccessLevel(profile.uaeAccessLevel) ? profile.uaeAccessLevel : 'SIGNED_UP';
};

// Owner 23 Oct 2026: a Live UAE agent (Emirates ID approved + agreement signed) gets full tool access right away.
// Tools read this level; payouts (UAE_PAYOUT_ACTIONS, held commission, cap year) keep the stored level.
export const uaeToolLevel = (profile: UaeLevelProfile): UaeAccessLevelValue | null => {
  const level = effectiveUaeLevel(profile);
  return level === 'VERIFIED' && profile?.uaeLive ? 'JOINED' : level;
};

export const uaeNextStep = (level: UaeAccessLevelValue | null): UaeNextStep =>
  level === 'SIGNED_UP' ? 'verify_id' : level === 'VERIFIED' ? 'join' : null;

export const UAE_GATED_ACTIONS = {
  REGISTER_DEAL: 'VERIFIED',
  ADVANCE_DEAL: 'VERIFIED',
  SEND_TO_CRM: 'VERIFIED',
  BOOK_UNIT: 'JOINED',
  // Exclusive unit holds (owner answer 13): open from Verified; the exclusives hold endpoint (ID-119) calls this.
  HOLD_EXCLUSIVE: 'VERIFIED',
  LETTERHEAD_DOC: 'JOINED',
  WITHDRAW: 'JOINED',
  // Pro features for UAE programme agents: visible at SIGNED_UP, usable from Verified.
  PRO_FEATURE: 'VERIFIED',
} as const satisfies Record<string, UaeAccessLevelValue>;
export type UaeGatedAction = keyof typeof UAE_GATED_ACTIONS;

// Money leaving the platform still waits for staff to approve the Join application.
export const UAE_PAYOUT_ACTIONS: readonly UaeGatedAction[] = ['WITHDRAW'];

export type UaeLevelDenial = {
  reason: 'uae_level_required';
  action: UaeGatedAction;
  requiredLevel: UaeAccessLevelValue;
  currentLevel: UaeAccessLevelValue;
  nextStep: Exclude<UaeNextStep, null>;
};

export const checkUaeLevel = (profile: UaeLevelProfile, action: UaeGatedAction): UaeLevelDenial | null => {
  const level = UAE_PAYOUT_ACTIONS.includes(action) ? effectiveUaeLevel(profile) : uaeToolLevel(profile);
  if (!level) return null;
  const requiredLevel = UAE_GATED_ACTIONS[action];
  if (LEVEL_RANK[level] >= LEVEL_RANK[requiredLevel]) return null;
  return {
    reason: 'uae_level_required',
    action,
    requiredLevel,
    currentLevel: level,
    nextStep: level === 'SIGNED_UP' ? 'verify_id' : 'join',
  };
};

export type UaeLevelLimits = { aiQuestions: number; marketingPosts: number };
export type UaeLimitsConfig = { SIGNED_UP: UaeLevelLimits; VERIFIED: UaeLevelLimits };

export const DEFAULT_UAE_LIMITS: UaeLimitsConfig = {
  SIGNED_UP: { aiQuestions: FREE_TIER_LIMITS.chat, marketingPosts: FREE_TIER_LIMITS.marketing },
  VERIFIED: { aiQuestions: 10, marketingPosts: 3 },
};

const readLimit = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : fallback;

// Merge AppSettings.uaeLimits over the defaults, per level and field; invalid values fall back.
export const parseUaeLimits = (raw: unknown): UaeLimitsConfig => {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, Record<string, unknown> | undefined>) : {};
  const level = (key: keyof UaeLimitsConfig): UaeLevelLimits => ({
    aiQuestions: readLimit(src[key]?.aiQuestions, DEFAULT_UAE_LIMITS[key].aiQuestions),
    marketingPosts: readLimit(src[key]?.marketingPosts, DEFAULT_UAE_LIMITS[key].marketingPosts),
  });
  return { SIGNED_UP: level('SIGNED_UP'), VERIFIED: level('VERIFIED') };
};

const NO_USE: UaeLevelLimits = { aiQuestions: 0, marketingPosts: 0 };

// Monthly limits for a level (null = Pro, no UAE cap). Programme: none before Verified, Pro after; partners: the config.
export const uaeLimitsFor = (
  level: UaeAccessLevelValue,
  config: UaeLimitsConfig,
  signupPath?: string | null,
): UaeLevelLimits | null => {
  if (level === 'JOINED') return null;
  if (!isUaePartnerPath(signupPath)) return level === 'SIGNED_UP' ? NO_USE : null;
  return config[level];
};

export type UaeLimitFeature = 'aiQuestions' | 'marketingPosts';
export type UaeLimitDenial = {
  reason: 'uae_limit_reached';
  feature: UaeLimitFeature;
  limit: number;
  nextStep: 'join' | 'verify_id';
};

export const checkUaeLimit = (
  level: UaeAccessLevelValue,
  feature: UaeLimitFeature,
  used: number,
  config: UaeLimitsConfig,
  signupPath?: string | null,
): UaeLimitDenial | null => {
  const limits = uaeLimitsFor(level, config, signupPath);
  if (!limits || used < limits[feature]) return null;
  return {
    reason: 'uae_limit_reached',
    feature,
    limit: limits[feature],
    nextStep: level === 'SIGNED_UP' ? 'verify_id' : 'join',
  };
};

export const usageLine = (used: number, limit: number) => ({ used, limit, remaining: Math.max(0, limit - used) });

export type LevelTransitionPlan =
  | { kind: 'error'; statusCode: 400 | 409; message: string }
  | { kind: 'noop' }
  | { kind: 'change'; from: UaeAccessLevelValue | null; to: UaeAccessLevelValue };

const AUTOMATIC_TRANSITIONS: Record<
  Exclude<UaeLevelSource, 'ADMIN'>,
  { from: (UaeAccessLevelValue | null)[]; to: UaeAccessLevelValue }
> = {
  SIGNUP: { from: [null], to: 'SIGNED_UP' },
  ID_UPLOAD: { from: ['SIGNED_UP'], to: 'VERIFIED' },
  ID_REJECTED: { from: ['VERIFIED', 'SIGNED_UP'], to: 'SIGNED_UP' },
  JOIN_APPROVED: { from: ['VERIFIED'], to: 'JOINED' },
};

// Allowed moves: signup → SIGNED_UP, upload → VERIFIED, rejection → SIGNED_UP, join → JOINED; admin any with a reason. JOINED is never lowered automatically.
export const planLevelTransition = (
  from: UaeAccessLevelValue | null,
  to: unknown,
  source: UaeLevelSource,
  reason?: string | null,
): LevelTransitionPlan => {
  if (!isUaeAccessLevel(to))
    return { kind: 'error', statusCode: 400, message: 'Level must be SIGNED_UP, VERIFIED or JOINED' };
  if (from === to) return { kind: 'noop' };
  if (source === 'ADMIN') {
    if ((reason ?? '').trim().length < MIN_UAE_LEVEL_REASON_LENGTH) {
      return {
        kind: 'error',
        statusCode: 400,
        message: `A reason of at least ${MIN_UAE_LEVEL_REASON_LENGTH} characters is required`,
      };
    }
    return { kind: 'change', from, to };
  }
  const rule = AUTOMATIC_TRANSITIONS[source];
  if (rule.to !== to || !rule.from.includes(from)) {
    return { kind: 'error', statusCode: 409, message: `Cannot move from ${from ?? 'no level'} to ${to} (${source})` };
  }
  return { kind: 'change', from, to };
};

// Partner path: Joined gets PRO access, earlier levels stay PREVIEW with UAE limits.
export const uaeLevelTier = (level: UaeAccessLevelValue): 'PREVIEW' | 'PRO' => (level === 'JOINED' ? 'PRO' : 'PREVIEW');

// Actions a SIGNED_UP programme agent cannot take until the Emirates ID is verified; all content stays visible.
export const UAE_VERIFY_LOCKED_FEATURES: readonly Feature[] = [
  'dealRegistration',
  'basicMarketing',
  'aiChat',
  'unlimitedAi',
  'marketingStudio',
  'exclusiveHold',
];

// Programme capability map: everything from Verified; before that every feature except the actions above.
export const uaeProgrammeCanMap = (level: UaeAccessLevelValue): Record<Feature, boolean> => {
  const out = {} as Record<Feature, boolean>;
  for (const feature of ALL_FEATURES)
    out[feature] = level !== 'SIGNED_UP' || !UAE_VERIFY_LOCKED_FEATURES.includes(feature);
  return out;
};
